// ======================================================
// UK Delivery Date Calculator
// Rule:
// - Estimated delivery: 2–3 working days
// - Order date itself is NOT counted
// - Exclude Saturday / Sunday
// - Exclude England & Wales Bank Holidays
// - Date calculation is always based on Europe/London
// - Bank Holiday data maintained through 2030-12-31
// ======================================================


// England & Wales Bank Holidays
const ukBankHolidays = new Set([
    // 2026
    '2026-01-01', // New Year's Day
    '2026-04-03', // Good Friday
    '2026-04-06', // Easter Monday
    '2026-05-04', // Early May bank holiday
    '2026-05-25', // Spring bank holiday
    '2026-08-31', // Summer bank holiday
    '2026-12-25', // Christmas Day
    '2026-12-28', // Boxing Day (substitute day)

    // 2027
    '2027-01-01', // New Year's Day
    '2027-03-26', // Good Friday
    '2027-03-29', // Easter Monday
    '2027-05-03', // Early May bank holiday
    '2027-05-31', // Spring bank holiday
    '2027-08-30', // Summer bank holiday
    '2027-12-27', // Christmas Day (substitute day)
    '2027-12-28', // Boxing Day (substitute day)

    // 2028
    '2028-01-03', // New Year's Day (substitute day)
    '2028-04-14', // Good Friday
    '2028-04-17', // Easter Monday
    '2028-05-01', // Early May bank holiday
    '2028-05-29', // Spring bank holiday
    '2028-08-28', // Summer bank holiday
    '2028-12-25', // Christmas Day
    '2028-12-26', // Boxing Day

    // 2029
    '2029-01-01', // New Year's Day
    '2029-03-30', // Good Friday
    '2029-04-02', // Easter Monday
    '2029-05-07', // Early May bank holiday
    '2029-05-28', // Spring bank holiday
    '2029-08-27', // Summer bank holiday
    '2029-12-25', // Christmas Day
    '2029-12-26', // Boxing Day

    // 2030
    '2030-01-01', // New Year's Day
    '2030-04-19', // Good Friday
    '2030-04-22', // Easter Monday
    '2030-05-06', // Early May bank holiday
    '2030-05-27', // Spring bank holiday
    '2030-08-26', // Summer bank holiday
    '2030-12-25', // Christmas Day
    '2030-12-26'  // Boxing Day
]);


// Holiday list is intentionally maintained only through this date.
// Dates after this will NOT exclude Bank Holidays.
const HOLIDAY_DATA_UNTIL = '2030-12-31';

let holidayDataWarningShown = false;


// ======================================================
// Get today's calendar date based on Europe/London
// ======================================================

function getLondonToday() {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).formatToParts(new Date());

    const values = {};

    parts.forEach(part => {
        if (part.type !== 'literal') {
            values[part.type] = part.value;
        }
    });

    // Use UTC midnight as a "pure calendar date".
    // All calculations below therefore use UTC methods.
    return new Date(Date.UTC(
        Number(values.year),
        Number(values.month) - 1,
        Number(values.day)
    ));
}


// ======================================================
// Convert Date → YYYY-MM-DD
// ======================================================

function getDateKey(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
}


// ======================================================
// Check whether a date is a UK working day
// ======================================================

function isWorkingDay(date) {
    const dayOfWeek = date.getUTCDay();
    const dateKey = getDateKey(date);

    // After 2030 we intentionally stop excluding Bank Holidays.
    // Warn developers once so the data doesn't silently become outdated.
    if (
        dateKey > HOLIDAY_DATA_UNTIL &&
        !holidayDataWarningShown
    ) {
        console.warn(
            '[delivery] UK Bank Holiday data only covers dates through 2030-12-31.'
        );

        holidayDataWarningShown = true;
    }

    // Saturday / Sunday
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return false;
    }

    // England & Wales Bank Holiday
    if (ukBankHolidays.has(dateKey)) {
        return false;
    }

    return true;
}


// ======================================================
// Add working days
// ======================================================

function addWorkingDays(date, workingDays) {
    const result = new Date(date);
    let addedDays = 0;

    while (addedDays < workingDays) {
        // Order date itself does not count.
        // Always start counting from the next calendar day.
        result.setUTCDate(result.getUTCDate() + 1);

        if (isWorkingDay(result)) {
            addedDays++;
        }
    }

    return result;
}


// ======================================================
// Format displayed date
// Example: Wed 19 Aug
// ======================================================

function getFormattedDate(date) {
    return new Intl.DateTimeFormat('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC'
    })
        .format(date)
        .replace(',', '');
}


// ======================================================
// Calculate delivery dates
// ======================================================

function calculateDeliveryDates() {
    const baseDate = getLondonToday();

    // Estimated delivery:
    // 2–3 working days after order date
    const fromDate = addWorkingDays(baseDate, 2);
    const toDate = addWorkingDays(baseDate, 3);

    const fromDateEl = document.getElementById('fromDate');
    const toDateEl = document.getElementById('toDate');

    if (!fromDateEl || !toDateEl) {
        return;
    }

    fromDateEl.innerText = getFormattedDate(fromDate);
    toDateEl.innerText = getFormattedDate(toDate);
}


// ======================================================
// Initial page load
// ======================================================

if (document.readyState === 'loading') {
    document.addEventListener(
        'DOMContentLoaded',
        calculateDeliveryDates
    );
} else {
    calculateDeliveryDates();
}


// ======================================================
// Shopify Theme Editor section reload
// ======================================================

document.addEventListener(
    'shopify:section:load',
    calculateDeliveryDates
);